import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { useToast } from "../../ui/toast";

export interface TaskPatch {
  title?: string;
  notes?: string;
  due?: Date | null;
  assigneeUid?: string | null;
  visibility?: "private" | "workspace";
}

export type SaveState = { status: "idle" } | { status: "saving" } | { status: "saved" } | { status: "error"; message: string };

/**
 * Saves a task's changes as they happen (WP-053): each change is one `tasks.update` with only the
 * fields that changed. A failed save keeps its patch so "Retry" sends it again.
 */
export function useAutosave(workspaceId: string, taskId: string) {
  const trpc = useTRPC();
  const toast = useToast();
  const live = useRef(true);
  // A change flushed on the way out of the page can fail after the page is gone: say so here.
  const { mutate } = useMutation(
    trpc.tasks.update.mutationOptions({
      onError: (error) => {
        if (!live.current) toast({ tone: "error", message: `A change to a task was not saved: ${errorMessage(error)}` });
      },
    }),
  );
  const [state, setState] = useState<SaveState>({ status: "idle" });
  const failed = useRef<TaskPatch | null>(null);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const save = useCallback(
    (patch: TaskPatch) => {
      if (Object.keys(patch).length === 0) return;
      setState({ status: "saving" });
      mutate(
        { workspaceId, taskId, ...patch },
        {
          onSuccess: () => {
            failed.current = null;
            if (live.current) setState({ status: "saved" });
          },
          onError: (error) => {
            failed.current = patch;
            if (live.current) setState({ status: "error", message: errorMessage(error) });
          },
        },
      );
    },
    [mutate, workspaceId, taskId],
  );

  const retry = useCallback(() => {
    if (failed.current !== null) save(failed.current);
  }, [save]);

  return { state, save, retry };
}

/**
 * A text field edited here while the task also changes live elsewhere. The field follows the
 * stored value until the person edits it; once their edit is saved, the stored value catches up
 * and the field follows again. An edit someone else saves meanwhile never overwrites typing.
 */
export function useSyncedText(stored: string): [string, (next: string) => void] {
  const [value, setValue] = useState(stored);
  const base = useRef(stored);
  useEffect(() => {
    setValue((current) => (current === base.current ? stored : current));
    base.current = stored;
  }, [stored]);
  return [value, setValue];
}
